import React, { createContext, FC, useReducer, useEffect } from 'react';
import {
    FetchSynonymsOptions,
    Synonym,
    SynonymCreatePayload,
    SynonymPagination,
} from '@packages/models/api/synonyms-search-term';
import { synonymsReducer, SynonymsState, SynonymsAction, initialSynonymsState } from './synonyms.state';
import { SynonymsService } from './synonyms.service';

type SynonymsActionDispatcher = React.Dispatch<SynonymsAction>;

interface SynonymsProviderProps {
    children: React.ReactNode;
    synonymsService: SynonymsService;
    query?: string;
}

export const SynonymsStateContext = createContext<SynonymsState | undefined>(undefined);
export const SynonymsDispatchContext = createContext<SynonymsActionDispatcher | undefined>(undefined);

export const SynonymsProvider: FC<SynonymsProviderProps> = ({ children, synonymsService, query }) => {
    const [state, dispatch] = useReducer(synonymsReducer, initialSynonymsState);

    useEffect(() => {
        const abortController = new AbortController();

        fetchSynonyms(dispatch, synonymsService, {});

        return () => {
            abortController.abort();
        };
    }, [synonymsService, query]);

    return (
        <SynonymsStateContext.Provider value={state}>
            <SynonymsDispatchContext.Provider value={dispatch}>{children}</SynonymsDispatchContext.Provider>
        </SynonymsStateContext.Provider>
    );
};

export const fetchSynonyms = async (
    dispatch: SynonymsActionDispatcher,
    synonymsService: SynonymsService,
    options: FetchSynonymsOptions
) => {
    dispatch({ type: 'SET_LOADING', payload: { loading: true } });
    const { query, size = 10, page = 1, sort, direction } = options;

    try {
        const { data, totalCount } = await synonymsService.fetchSynonyms({
            query,
            page: page - 1,
            size,
            sort: sort,
            direction: direction?.toLowerCase(),
        });

        const newPagination: SynonymPagination = {
            page: page + 1,
            size: size,
            total: totalCount,
        };

        dispatch({ type: 'SET_SYNONYMS', payload: { synonyms: data, pagination: newPagination } });
    } catch (error: any) {
        dispatch({ type: 'SET_ERROR', payload: { error: error.message } });
    } finally {
        dispatch({ type: 'SET_LOADING', payload: { loading: false } });
    }
};

export const createSynonym = async (
    dispatch: SynonymsActionDispatcher,
    synonymsService: SynonymsService,
    payload: SynonymCreatePayload
) => {
    const searchTermId = Math.random().toString(36).substring(2, 15);
    dispatch({ type: 'SET_LOADING', payload: { loading: true } });
    try {
        dispatch({
            type: 'ADD_SYNONYM',
            payload: {
                synonym: {
                    ...payload,
                    searchTermId: searchTermId,
                    createAt: Date.now().toString(),
                    updateAt: Date.now().toString(),
                },
            },
        });

        const synonym = await synonymsService.createSynonym(payload);
        dispatch({ type: 'DELETE_SYNONYM', payload: { id: searchTermId } });
        dispatch({ type: 'ADD_SYNONYM', payload: { synonym } });
    } catch (error: any) {
        dispatch({ type: 'DELETE_SYNONYM', payload: { id: searchTermId } });
        dispatch({ type: 'SET_ERROR', payload: { error: error.message } });
    } finally {
        dispatch({ type: 'SET_LOADING', payload: { loading: false } });
    }
};

export const updateSynonym = async (
    dispatch: SynonymsActionDispatcher,
    synonymsService: SynonymsService,
    payload: Partial<SynonymCreatePayload> & { id: string }
) => {
    dispatch({ type: 'SET_LOADING', payload: { loading: true } });
    try {
        const { id, ...rest } = payload;
        const payloadWithoutNull = Object.fromEntries(Object.entries(rest).filter(([_, v]) => v != null));

        dispatch({
            type: 'UPDATE_SYNONYM',
            payload: { synonym: { ...payloadWithoutNull, searchTermId: payload.id } as unknown as Synonym },
        });
        const synonym = await synonymsService.updateSynonym(payload.id, payload);
        dispatch({ type: 'UPDATE_SYNONYM', payload: { synonym } });
    } catch (error: any) {
        dispatch({ type: 'SET_ERROR', payload: { error: error.message } });
        fetchSynonyms(dispatch, synonymsService, {});
    } finally {
        dispatch({ type: 'SET_LOADING', payload: { loading: false } });
    }
};

export const deleteSynonym = async (
    dispatch: SynonymsActionDispatcher,
    synonymsService: SynonymsService,
    synonym: Synonym
) => {
    dispatch({ type: 'SET_LOADING', payload: { loading: true } });
    try {
        dispatch({ type: 'DELETE_SYNONYM', payload: { id: synonym.searchTermId } });
        await synonymsService.deleteSynonym(synonym.searchTermId);
    } catch (error: any) {
        dispatch({ type: 'SET_ERROR', payload: { error: error.message } });
        fetchSynonyms(dispatch, synonymsService, {});
    } finally {
        dispatch({ type: 'SET_LOADING', payload: { loading: false } });
    }
};
